import React, {useContext, useState} from 'react'
import {useIntl} from 'react-intl'
import PropTypes from 'prop-types'
import {Box, Container, Stack, Text, IconButton, Link as ChakraLink} from '@chakra-ui/react'
import {CloseIcon} from '../../../components/custom-icons'

// Constants
import {COOKIE} from '../../../constants'

// Utils
import {useUrlNotLocalizedFromLink} from '../../helpers/urlHelper'
import Link from '@salesforce/retail-react-app/app/components/link'
import Cookies from 'js-cookie'
import {HeaderBannerStateContext} from '../../../custom-contexts'

/**
 * PDCHeaderTopBanner is a responsive React component that displays a dismissible top banner in the page header,
 * supporting dynamic messages and links based on the current viewport size. The banner can display different
 * messages and links for small, medium, and large breakpoints, and supports both internal and external links.
 * The visibility state of the banner is persisted in session storage to ensure a consistent user experience
 * across page reloads. The component also allows for custom link overrides via the `custom` prop.
 *
 * Props:
 * @param {string} smallMessage - The message to display on small screens.
 * @param {string} mediumMessage - The message to display on medium screens.
 * @param {string} largeMessage - The message to display on large screens.
 * @param {object} smallLink - The link object for small screens.
 * @param {object} mediumLink - The link object for medium screens.
 * @param {object} largeLink - The link object for large screens.
 * @param {object} custom - Optional custom link overrides for each breakpoint.
 *
 * The component uses Chakra UI for styling and layout, and integrates with internationalization and routing
 * utilities. The banner message can include a <link>...</link> tag, which will be replaced with the appropriate
 * link element. The banner can be closed by the user, and this state is remembered for the session.
 */
export const PDCHeaderTopBanner = ({
    smallMessage,
    mediumMessage,
    largeMessage,
    smallLink,
    mediumLink,
    largeLink,
    custom
}) => {
    const intl = useIntl()

    const updatedSmallLink = useUrlNotLocalizedFromLink(custom?.smallLink || smallLink) || smallLink
    const updatedMediumLink =
        useUrlNotLocalizedFromLink(custom?.mediumLink || mediumLink) || mediumLink
    const updatedLargeLink = useUrlNotLocalizedFromLink(custom?.largeLink || largeLink) || largeLink

    const isSmallLinkExternal = custom?.smallLink?.type === 'EXTERNAL'
    const isMediumLinkExternal = custom?.mediumLink?.type === 'EXTERNAL'
    const isLargeLinkExternal = custom?.largeLink?.type === 'EXTERNAL'

    // Get the visibility state of the header top banner
    const context = useContext(HeaderBannerStateContext)
    const {headerBannerState, setHeaderBannerState} = context
    const [showBanner, setShowBanner] = useState(() => {
        const isBannerVisible = headerBannerState === 'closed' ? false : true
        return smallMessage || mediumMessage || largeMessage ? isBannerVisible : false
    })

    /**
     * Asynchronously sets the visibility state of the header banner and updates the corresponding cookie.
     *
     * This function sets a cookie to indicate the banner's closed state, with an expiration date one year from the current time.
     * It also updates the local header banner state and toggles the banner's visibility in the UI.
     *
     * @async
     * @param {boolean} isVisible - Determines whether the banner should be shown or hidden.
     * @returns {Promise<void>} Resolves when the banner visibility and cookie state have been updated.
     */
    const setBannerVisibility = async (isVisible) => {
        const cookieExpirationTimestamp = new Date()
        cookieExpirationTimestamp.setFullYear(cookieExpirationTimestamp.getFullYear() + 1)
        Cookies.set(COOKIE.HEADER_BANNER_STATE, 'closed', {
            expires: cookieExpirationTimestamp
        })
        setHeaderBannerState('closed')

        setShowBanner(isVisible)
    }

    const renderMessageWithLink = (message, link, isLinkExternal) => {
        const linkTagRegex = /<link>(.*?)<\/link>/
        const parts = message.split(linkTagRegex)

        return (
            <span>
                {parts.map((part, index) => {
                    if (index % 2 === 1 && link) {
                        return isLinkExternal ? (
                            <ChakraLink key={index} href={link} isExternal color="white">
                                <Text as="span" variant="descriptor">
                                    {part}
                                </Text>
                            </ChakraLink>
                        ) : (
                            <Link key={index} to={link} color="white">
                                <Text as="span" variant="descriptor">
                                    {part}
                                </Text>
                            </Link>
                        )
                    }
                    return part.length > 0 ? (
                        <span key={index} dangerouslySetInnerHTML={{__html: part}} />
                    ) : (
                        part
                    )
                })}
            </span>
        )
    }

    return (
        showBanner && (
            <Box as="section" maxWidth="container.xxxl" mx="auto" id="headerBanner">
                <Box color="white" bg="blue" position={'relative'}>
                    <Container
                        py={['10px', null, '6px']}
                        pl={['10px', null, '16px', null, '40px']}
                        pr={['32px', null, '34px', null, '58px']}
                    >
                        <Stack direction="row" justify="center" align={'center'}>
                            <Text variant="descriptor" display={['block', null, 'none', 'none']}>
                                {renderMessageWithLink(
                                    smallMessage,
                                    updatedSmallLink,
                                    isSmallLinkExternal
                                )}
                            </Text>
                            <Text variant="descriptor" display={['none', null, 'block', 'none']}>
                                {renderMessageWithLink(
                                    mediumMessage || smallMessage,
                                    updatedMediumLink,
                                    isMediumLinkExternal
                                )}
                            </Text>
                            <Text variant="descriptor" display={['none', null, 'none', 'block']}>
                                {renderMessageWithLink(
                                    largeMessage || mediumMessage || smallMessage,
                                    updatedLargeLink,
                                    isLargeLinkExternal
                                )}
                            </Text>
                            <IconButton
                                position={'absolute'}
                                right={['0', null, '10px', null, '34px']}
                                padding={['10px', null, '6px']}
                                size="auto"
                                icon={<CloseIcon boxSize={3} />}
                                aria-label={intl.formatMessage({
                                    defaultMessage: 'Close banner',
                                    id: 'header.top_banner.close'
                                })}
                                onClick={() => setBannerVisibility(false)}
                            />
                        </Stack>
                    </Container>
                </Box>
            </Box>
        )
    )
}

PDCHeaderTopBanner.propTypes = {
    smallMessage: PropTypes.string,
    mediumMessage: PropTypes.string,
    largeMessage: PropTypes.string,
    smallLink: PropTypes.string,
    mediumLink: PropTypes.string,
    largeLink: PropTypes.string,
    custom: PropTypes.shape({
        smallLink: PropTypes.shape({
            target: PropTypes.string,
            type: PropTypes.string
        }),
        mediumLink: PropTypes.shape({
            target: PropTypes.string,
            type: PropTypes.string
        }),
        largeLink: PropTypes.shape({
            target: PropTypes.string,
            type: PropTypes.string
        })
    })
}
